import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useBreakpointValue,
  Tooltip,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ViewIcon, ArrowBackIcon } from "@chakra-ui/icons";
import axios from "axios";
import proxy from "../../proxy";

// ─── Helpers ─────────────────────────────────────────────────
const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

const daysAgo = (dateStr) => {
  const d = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  return `${d}d ago`;
};

// ─── Insight Detail Modal ────────────────────────────────────
const InsightModal = ({ insight, isOpen, onClose, type }) => {
  if (!insight) return null;

  const change = parseFloat(insight.daily_change || 0);
  const isPositive = change >= 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", sm: "lg" }} isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        bg="gray.800"
        border="1px solid"
        borderColor="whiteAlpha.200"
        borderRadius={{ base: "none", sm: "xl" }}
      >
        <ModalHeader>
          <HStack spacing={2}>
            <Badge
              bg={type === "daily"
                ? "linear-gradient(135deg, #9F7AEA, #6B46C1)"
                : "linear-gradient(135deg, #48BB78, #38A169)"}
              color="white"
              px={2}
              py={0.5}
              borderRadius="full"
              fontSize="xs"
            >
              {type === "daily" ? "🤖 Daily Insight" : "🏋️ Weekly Coach"}
            </Badge>
            <Text color="gray.400" fontSize="sm">
              {formatDate(insight.created_at)}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="whiteAlpha.600" />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {/* Message */}
            <Text color="gray.200" fontSize="md" lineHeight="tall">
              {insight.insight_text}
            </Text>

            {/* Stats */}
            <SimpleGrid columns={type === "daily" ? 3 : 2} spacing={3}>
              {insight.portfolio_value && (
                <Box bg="whiteAlpha.100" p={3} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xs" color="gray.500">Portfolio Value</Text>
                  <Text fontSize="lg" fontWeight="bold" color="blue.300">
                    ${Number(insight.portfolio_value).toFixed(2)}
                  </Text>
                </Box>
              )}
              {type === "daily" && (
                <Box bg="whiteAlpha.100" p={3} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xs" color="gray.500">Daily Change</Text>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={isPositive ? "green.300" : "red.300"}
                  >
                    {isPositive ? "+" : ""}${change.toFixed(2)}
                  </Text>
                </Box>
              )}
              {insight.monthly_dividends && (
                <Box bg="whiteAlpha.100" p={3} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xs" color="gray.500">Monthly Div</Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.300">
                    ${Number(insight.monthly_dividends).toFixed(2)}
                  </Text>
                </Box>
              )}
              {insight.milestones_reached && (
                <Box bg="whiteAlpha.100" p={3} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xs" color="gray.500">Milestones</Text>
                  <Text fontSize="lg" fontWeight="bold" color="teal.300">
                    {insight.milestones_reached}
                  </Text>
                </Box>
              )}
              <Box bg="whiteAlpha.100" p={3} borderRadius="lg" textAlign="center">
                <Text fontSize="2xs" color="gray.500">Generated</Text>
                <Text fontSize="sm" fontWeight="bold" color="gray.300">
                  {formatTime(insight.created_at)}
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ─── Mobile Card ─────────────────────────────────────────────
const InsightCard = ({ insight, type, onView }) => {
  const change = parseFloat(insight.daily_change || 0);
  const isPositive = change >= 0;

  return (
    <Box
      p={3}
      bg="rgba(255,255,255,0.03)"
      borderRadius="xl"
      border="1px solid rgba(255,255,255,0.08)"
      transition="all 0.25s"
      _hover={{ bg: "rgba(255,255,255,0.06)" }}
      cursor="pointer"
      onClick={() => onView(insight)}
    >
      <HStack justify="space-between" mb={2}>
        <Badge
          bg={type === "daily"
            ? "linear-gradient(135deg, #9F7AEA, #6B46C1)"
            : "linear-gradient(135deg, #48BB78, #38A169)"}
          color="white"
          fontSize="2xs"
          px={1.5}
          borderRadius="full"
        >
          {type === "daily" ? "🤖 Daily" : "🏋️ Weekly"}
        </Badge>
        <Text fontSize="xs" color="gray.500">
          {daysAgo(insight.created_at)}
        </Text>
      </HStack>

      <Text fontSize="xs" color="gray.300" noOfLines={3} mb={2}>
        {insight.insight_text}
      </Text>

      <HStack spacing={2}>
        {insight.portfolio_value && (
          <Badge colorScheme="blue" fontSize="2xs" borderRadius="md">
            ${Number(insight.portfolio_value).toFixed(0)}
          </Badge>
        )}
        {type === "daily" && change !== 0 && (
          <Badge
            colorScheme={isPositive ? "green" : "red"}
            fontSize="2xs"
            borderRadius="md"
          >
            {isPositive ? "↑" : "↓"}${Math.abs(change).toFixed(2)}
          </Badge>
        )}
        {insight.milestones_reached && (
          <Badge colorScheme="teal" fontSize="2xs" borderRadius="md">
            {insight.milestones_reached} milestones
          </Badge>
        )}
      </HStack>
    </Box>
  );
};

// ─── Desktop Table Row ───────────────────────────────────────
const InsightRow = ({ insight, type, onView }) => {
  const change = parseFloat(insight.daily_change || 0);
  const isPositive = change >= 0;

  return (
    <Tr
      _hover={{ bg: "whiteAlpha.50" }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={() => onView(insight)}
    >
      <Td fontSize="sm" color="gray.400" textAlign="center">
        {formatDate(insight.created_at)}
      </Td>
      <Td fontSize="sm" color="gray.400" textAlign="center">
        {formatTime(insight.created_at)}
      </Td>
      <Td fontSize="xs" color="gray.300" maxW="400px">
        <Text noOfLines={2}>{insight.insight_text}</Text>
      </Td>
      {type === "daily" && (
        <>
          <Td textAlign="center" fontSize="sm" color="blue.300" fontWeight="bold">
            {insight.portfolio_value ? `$${Number(insight.portfolio_value).toFixed(2)}` : "—"}
          </Td>
          <Td textAlign="center" fontSize="sm" fontWeight="bold"
            color={isPositive ? "green.300" : "red.300"}
          >
            {change !== 0 ? `${isPositive ? "+" : ""}$${change.toFixed(2)}` : "—"}
          </Td>
        </>
      )}
      {type === "weekly" && (
        <>
          <Td textAlign="center" fontSize="sm" color="green.300" fontWeight="bold">
            {insight.monthly_dividends ? `$${Number(insight.monthly_dividends).toFixed(2)}` : "—"}
          </Td>
          <Td textAlign="center" fontSize="sm" color="teal.300" fontWeight="bold">
            {insight.milestones_reached || "—"}
          </Td>
        </>
      )}
      <Td textAlign="center">
        <Tooltip label="View details">
          <IconButton
            icon={<ViewIcon />}
            size="xs"
            variant="ghost"
            color="whiteAlpha.600"
            _hover={{ color: "teal.300" }}
            onClick={(e) => { e.stopPropagation(); onView(insight); }}
            aria-label="View"
          />
        </Tooltip>
      </Td>
    </Tr>
  );
};

// ─── Main Component ──────────────────────────────────────────
const AdminInsights = () => {
  const [dailyInsights, setDailyInsights] = useState([]);
  const [weeklyCoach, setWeeklyCoach] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [selectedType, setSelectedType] = useState("daily");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dailyRes, weeklyRes] = await Promise.all([
          axios.get(`${proxy}server_mts/admin/GetAllInsights.php`, {
            headers: { "Cache-Control": "no-cache" },
          }),
          axios.get(`${proxy}server_mts/admin/GetAllWeeklyCoach.php`, {
            headers: { "Cache-Control": "no-cache" },
          }).catch(() => ({ data: [] })),
        ]);

        setDailyInsights(Array.isArray(dailyRes.data) ? dailyRes.data : []);
        setWeeklyCoach(Array.isArray(weeklyRes.data) ? weeklyRes.data : []);
      } catch (err) {
        setError("Unable to load AI insights.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleView = (insight, type) => {
    setSelectedInsight(insight);
    setSelectedType(type);
    onOpen();
  };

  // Stats
  const stats = useMemo(() => {
    const avgChange = dailyInsights.length > 0
      ? dailyInsights.reduce((sum, d) => sum + parseFloat(d.daily_change || 0), 0) / dailyInsights.length
      : 0;
    const latestValue = dailyInsights.length > 0
      ? parseFloat(dailyInsights[0].portfolio_value || 0)
      : 0;
    const positiveDays = dailyInsights.filter(d => parseFloat(d.daily_change || 0) > 0).length;

    return {
      totalDaily: dailyInsights.length,
      totalWeekly: weeklyCoach.length,
      avgChange,
      latestValue,
      positiveDays,
      totalInsights: dailyInsights.length + weeklyCoach.length,
    };
  }, [dailyInsights, weeklyCoach]);

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.400" thickness="4px" />
            <Text color="gray.400">Loading AI insights...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={3}>
            <Text fontSize="4xl">⚠️</Text>
            <Text color="red.400" fontWeight="bold">{error}</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={4}>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
                aria-label="Back to dashboard"
              />
              <VStack align="start" spacing={0}>
                <Heading
                  size="md"
                  bgGradient="linear(to-r, teal.300, cyan.400)"
                  bgClip="text"
                >
                  🤖 AI Insights History
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Daily & weekly AI-generated portfolio insights
                </Text>
              </VStack>
            </HStack>

            <HStack spacing={2}>
              <Badge colorScheme="purple" fontSize="xs" px={2} py={1}>
                {stats.totalDaily} daily
              </Badge>
              <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                {stats.totalWeekly} weekly
              </Badge>
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={6}>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">

        {/* Summary Stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 2, md: 4 }}>
          <Card bg="gray.800" borderWidth="1px" borderColor="gray.700">
            <CardBody p={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">Total Insights</StatLabel>
                <StatNumber color="purple.300" fontSize={{ base: "lg", md: "xl" }}>
                  {stats.totalInsights}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  {stats.totalDaily} daily + {stats.totalWeekly} weekly
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="gray.800" borderWidth="1px" borderColor="gray.700">
            <CardBody p={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">Latest Value</StatLabel>
                <StatNumber color="blue.300" fontSize={{ base: "lg", md: "xl" }}>
                  ${stats.latestValue.toFixed(0)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">Portfolio</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="gray.800" borderWidth="1px" borderColor="gray.700">
            <CardBody p={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">Avg Daily Change</StatLabel>
                <StatNumber
                  color={stats.avgChange >= 0 ? "green.300" : "red.300"}
                  fontSize={{ base: "lg", md: "xl" }}
                >
                  {stats.avgChange >= 0 ? "+" : ""}${stats.avgChange.toFixed(2)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">Per day</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="gray.800" borderWidth="1px" borderColor="gray.700">
            <CardBody p={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">Positive Days</StatLabel>
                <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                  {stats.positiveDays}/{stats.totalDaily}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  {stats.totalDaily > 0 ? Math.round((stats.positiveDays / stats.totalDaily) * 100) : 0}% win rate
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Tabs */}
        <Tabs variant="soft-rounded" colorScheme="teal">
          <TabList mb={4} justifyContent="center">
            <Tab fontSize="sm" fontWeight="semibold">
              🤖 Daily ({dailyInsights.length})
            </Tab>
            <Tab fontSize="sm" fontWeight="semibold">
              🏋️ Weekly ({weeklyCoach.length})
            </Tab>
          </TabList>

          <TabPanels>
            {/* Daily Insights Tab */}
            <TabPanel p={0}>
              <Box
                p={{ base: 3, md: 4 }}
                borderWidth="1px"
                borderColor="gray.700"
                borderRadius="lg"
                bg="gray.800"
                overflowX="auto"
              >
                {dailyInsights.length === 0 ? (
                  <Center py={10}>
                    <Text color="gray.500">No daily insights yet. Run the cron to generate one.</Text>
                  </Center>
                ) : isMobile ? (
                  <VStack spacing={2} align="stretch">
                    {dailyInsights.map((insight) => (
                      <InsightCard
                        key={insight.id}
                        insight={insight}
                        type="daily"
                        onView={(i) => handleView(i, "daily")}
                      />
                    ))}
                  </VStack>
                ) : (
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.900">
                      <Tr>
                        <Th color="teal.200" fontSize="xs" textAlign="center">Date</Th>
                        <Th color="teal.200" fontSize="xs" textAlign="center">Time</Th>
                        <Th color="teal.200" fontSize="xs">Insight</Th>
                        <Th color="teal.200" fontSize="xs" textAlign="center">Value</Th>
                        <Th color="teal.200" fontSize="xs" textAlign="center">Change</Th>
                        <Th color="teal.200" fontSize="xs" textAlign="center" w="50px"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dailyInsights.map((insight) => (
                        <InsightRow
                          key={insight.id}
                          insight={insight}
                          type="daily"
                          onView={(i) => handleView(i, "daily")}
                        />
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>

            {/* Weekly Coach Tab */}
            <TabPanel p={0}>
              <Box
                p={{ base: 3, md: 4 }}
                borderWidth="1px"
                borderColor="gray.700"
                borderRadius="lg"
                bg="gray.800"
                overflowX="auto"
              >
                {weeklyCoach.length === 0 ? (
                  <Center py={10}>
                    <Text color="gray.500">No weekly coach messages yet. Run the cron to generate one.</Text>
                  </Center>
                ) : isMobile ? (
                  <VStack spacing={2} align="stretch">
                    {weeklyCoach.map((coach) => (
                      <InsightCard
                        key={coach.id}
                        insight={coach}
                        type="weekly"
                        onView={(i) => handleView(i, "weekly")}
                      />
                    ))}
                  </VStack>
                ) : (
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.900">
                      <Tr>
                        <Th color="green.200" fontSize="xs" textAlign="center">Date</Th>
                        <Th color="green.200" fontSize="xs" textAlign="center">Time</Th>
                        <Th color="green.200" fontSize="xs">Message</Th>
                        <Th color="green.200" fontSize="xs" textAlign="center">Monthly Div</Th>
                        <Th color="green.200" fontSize="xs" textAlign="center">Milestones</Th>
                        <Th color="green.200" fontSize="xs" textAlign="center" w="50px"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {weeklyCoach.map((coach) => (
                        <InsightRow
                          key={coach.id}
                          insight={coach}
                          type="weekly"
                          onView={(i) => handleView(i, "weekly")}
                        />
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
        </VStack>
      </Container>

      {/* Detail Modal */}
      <InsightModal
        insight={selectedInsight}
        isOpen={isOpen}
        onClose={onClose}
        type={selectedType}
      />
    </Box>
  );
};

export default AdminInsights;